/**
 * Pasted text: which parts become link chips, which a note chip, which stay
 * in the bar as words.
 *
 *  - Every web address in the paste becomes a link chip (normalized:
 *    shared/src/utils/link-url.ts); the words around it stay in the bar.
 *  - What remains becomes a note chip when it reads like a note: a list of
 *    three lines or more, two lines of some length, or simply long text (a
 *    list copied from Notes, a spec sheet). Line breaks are kept.
 *  - Anything else is ordinary typing: the browser pastes it as usual.
 *
 * Pure, so it is unit-tested under the repo's node jest.
 */

import { normalizeLinkUrl } from 'shared/src/utils/link-url';

/** http(s) addresses and bare www. ones. */
const URL_IN_TEXT = /(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;

/** A multi-line paste this long, or any paste longer than NOTE_PASTE_LONG, is a note. */
export const NOTE_PASTE_MULTILINE = 120;
export const NOTE_PASTE_LONG = 300;

function trimTrailing(match: string): string {
  let url = match.replace(/[.,;:!?'"»”’]+$/, '');
  // A closing bracket belongs to the URL only when it opened one: (see https://x.com/a_(b)).
  for (const [open, close] of [['(', ')'], ['[', ']'], ['{', '}']] as const) {
    while (url.endsWith(close) && url.split(open).length < url.split(close).length) {
      url = url.slice(0, -1).replace(/[.,;:!?'"]+$/, '');
    }
  }
  return url;
}

export interface LinkMatch {
  start: number;
  /** Exclusive. */
  end: number;
  /** The address as written. */
  raw: string;
  /** Its normalized form (shared/src/utils/link-url.ts). */
  url: string;
}

/** Every web address written in a text, where it sits, in order. */
export function scanLinks(text: string): LinkMatch[] {
  const out: LinkMatch[] = [];
  const re = new RegExp(URL_IN_TEXT.source, 'gi');
  for (let match = re.exec(text); match; match = re.exec(text)) {
    const raw = trimTrailing(match[0]);
    const normalized = normalizeLinkUrl(raw);
    if (!normalized) continue;
    out.push({ start: match.index, end: match.index + raw.length, raw, url: normalized.url });
  }
  return out;
}

/** The normalized web addresses in a text, in order, deduped, and the text without them. */
export function extractLinksFromText(text: string): { urls: string[]; rest: string } {
  const urls: string[] = [];
  let rest = '';
  let cursor = 0;
  for (const link of scanLinks(text)) {
    if (!urls.includes(link.url)) urls.push(link.url);
    rest += `${text.slice(cursor, link.start)} `;
    cursor = link.end;
  }
  return { urls, rest: urls.length > 0 ? rest + text.slice(cursor) : text };
}

/**
 * The text with each address replaced by `replacer(url)` where it was
 * written; a null answer leaves that address as it is.
 */
export function replaceLinksInText(text: string, replacer: (url: string) => string | null): string {
  let out = '';
  let cursor = 0;
  for (const link of scanLinks(text)) {
    const replacement = replacer(link.url);
    if (replacement === null) continue;
    out += text.slice(cursor, link.start) + replacement;
    cursor = link.end;
  }
  return out + text.slice(cursor);
}

/** A list: this many non-empty lines is a note however short. */
export const NOTE_PASTE_LINES = 3;

function looksLikeNote(text: string): boolean {
  const trimmed = text.trim();
  const lines = trimmed.split(/\r?\n/).filter((line) => line.trim()).length;
  return (
    lines >= NOTE_PASTE_LINES ||
    (lines > 1 && trimmed.length > NOTE_PASTE_MULTILINE) ||
    trimmed.length > NOTE_PASTE_LONG
  );
}

export interface PasteClassification {
  /** Link chips to add. */
  urls: string[];
  /** A note chip to add (line breaks kept), or null. */
  note: string | null;
  /** Words to insert in the bar, or null. */
  text: string | null;
}

/**
 * How to treat a paste. When `urls` is empty and `note` is null the paste is
 * ordinary text and the caller leaves it to the browser.
 */
export function classifyPaste(raw: string): PasteClassification {
  const { urls, rest } = extractLinksFromText(raw);
  if (looksLikeNote(rest)) {
    return { urls, note: rest.replace(/\r\n?/g, '\n').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim(), text: null };
  }
  const words = rest.replace(/\s+/g, ' ').trim();
  return { urls, note: null, text: words || null };
}
